/**
 * 导出面板（M2-19 / §6.5，补充参照 Photoshop「导出为」/「存储为 Web 所用
 * 格式」）：格式 / 质量 / 颜色位数（PNG-8 调色板）/ 分辨率 / 实时预览。
 * 分辨率直接显示宽/高像素输入框（可手动改），底部三个快捷倍率按钮
 * 只是把宽高改写成对应值，不是"批量勾选多个尺寸一次导出"——每次只
 * 导出当前输入框里的这一个尺寸，界面上不再需要额外的"添加"/清单。
 * 全部取值来自设计 token（§7.2，tokens.test.ts 扫描）。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import type { ExportFormat } from '../../app/export';
import styles from './ExportDialog.module.css';

export interface ExportRequest {
  readonly format: ExportFormat;
  readonly quality: number;
  readonly scales: readonly number[];
  /** 手动输入的像素宽高——跟 `scales` 是两条独立的路径，不要求跟原图等比。 */
  readonly customSizes: readonly { readonly width: number; readonly height: number }[];
  /** png8 专用：调色板颜色数 2–256。其它格式忽略。 */
  readonly colors: number;
  /** png8 专用：Floyd–Steinberg 误差扩散仿色。其它格式忽略。 */
  readonly dither: boolean;
}

/** 触发预览重算所需的最小信息——分辨率不影响预览缩略图本身，不含在内。 */
export interface ExportPreviewRecipe {
  readonly format: ExportFormat;
  readonly quality: number;
  readonly colors: number;
  readonly dither: boolean;
}

export interface ExportDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onExport: (request: ExportRequest) => void;
  /** 上一次导出的产出说明（成功反馈）。 */
  readonly summary?: string | undefined;
  /** 文档像素尺寸——用来把倍率换算成"实际导出多大"显示给用户。 */
  readonly documentSize?: { readonly width: number; readonly height: number } | undefined;
  /**
   * 格式/质量/颜色数/仿色任一变化都会调这个（装配层做防抖+真实编码估算，
   * UI 层自己不摸像素——CLAUDE.md 规则 4）。省略则不出现预览区。
   */
  readonly onPreviewRequest?: ((recipe: ExportPreviewRecipe) => void) | undefined;
  /** 预览缩略图，data URL。 */
  readonly previewUrl?: string | undefined;
  /** 预览体积文案，如"约 12.4 KB"。 */
  readonly previewSize?: string | undefined;
  /** 预览正在重算（防抖等待/编码中）。 */
  readonly previewPending?: boolean | undefined;
}

const PRESET_SCALES = [0.5, 1, 2] as const;

export function ExportDialog(props: ExportDialogProps): JSX.Element {
  const [format, setFormat] = createSignal<ExportFormat>('png');
  const [quality, setQuality] = createSignal(90);
  const [colors, setColors] = createSignal(256);
  const [dither, setDither] = createSignal(true);
  const [width, setWidth] = createSignal('');
  const [height, setHeight] = createSignal('');
  const [lockAspect, setLockAspect] = createSignal(true);

  // 打开面板时把宽高填成文档原始尺寸（等价于 1x）——不留空，用户
  // 一眼就能看到"要导出多大"，需要改再改。
  let wasOpen = false;
  createEffect(() => {
    const size = props.documentSize;
    if (props.open && !wasOpen && size !== undefined) {
      setWidth(String(size.width));
      setHeight(String(size.height));
    }
    wasOpen = props.open;
  });

  // "等比"勾选时按文档宽高比联动：改宽度时算出对应高度并回填输入框，
  // 反之亦然，默认勾选。取消勾选后两个输入框各自独立，允许拉伸/非等比
  // 导出——导出管线的 `resample`（Lanczos）本身就是任意 dw/dh，等比
  // 只是这个输入框默认的联动行为，不是管线的硬限制。
  const setWidthPx = (value: string): void => {
    setWidth(value);
    if (!lockAspect()) return;
    const size = props.documentSize;
    const w = Number(value);
    if (size === undefined || !Number.isFinite(w) || w <= 0) return;
    setHeight(String(Math.round((w / size.width) * size.height)));
  };

  const setHeightPx = (value: string): void => {
    setHeight(value);
    if (!lockAspect()) return;
    const size = props.documentSize;
    const h = Number(value);
    if (size === undefined || !Number.isFinite(h) || h <= 0) return;
    setWidth(String(Math.round((h / size.height) * size.width)));
  };

  // 快捷倍率按钮：直接把宽高改写成"文档原始尺寸 × 倍率"，跟等比开关
  // 无关——按下按钮这个动作本身就是在选一个等比的目标尺寸。
  const applyPresetScale = (scale: number): void => {
    const size = props.documentSize;
    if (size === undefined) return;
    setWidth(String(Math.max(1, Math.round(size.width * scale))));
    setHeight(String(Math.max(1, Math.round(size.height * scale))));
  };

  // 格式/质量/颜色数/仿色任一变化都通知装配层重算预览——分辨率不影响
  // 缩略图本身（预览面板始终按同一个基准尺寸显示），故不依赖宽高
  createEffect(() => {
    if (!props.open || format() === 'psd') return;
    props.onPreviewRequest?.({
      format: format(),
      quality: quality(),
      colors: colors(),
      dither: dither(),
    });
  });

  return (
    <Show when={props.open}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="export-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          aria-label="导出"
          data-testid="export-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>导出</h2>

          <Show when={props.onPreviewRequest !== undefined && format() !== 'psd'}>
            <div class={styles.previewPanel}>
              <Show
                when={props.previewUrl !== undefined}
                fallback={<div class={styles.previewPlaceholder}>预览生成中…</div>}
              >
                <img class={styles.previewImage} src={props.previewUrl} alt="导出预览" data-testid="export-preview-image" />
              </Show>
              <span class={styles.previewSize} data-testid="export-preview-size">
                {props.previewPending === true ? '计算中…' : props.previewSize ?? ''}
              </span>
            </div>
          </Show>

          <label class={styles.row}>
            <span class={styles.label}>格式</span>
            <select
              class={styles.select}
              value={format()}
              data-testid="export-format"
              onChange={(event) => {
                setFormat(event.currentTarget.value as ExportFormat);
              }}
            >
              <option value="png">PNG</option>
              <option value="png8">PNG-8（调色板）</option>
              <option value="jpeg">JPEG</option>
              <option value="psd">PSD（Photoshop，保留图层）</option>
            </select>
          </label>

          <Show when={format() === 'jpeg'}>
            <label class={styles.row}>
              <span class={styles.label}>质量</span>
              <input
                type="range"
                min="1"
                max="100"
                value={quality()}
                data-testid="export-quality"
                onInput={(event) => {
                  setQuality(Number(event.currentTarget.value));
                }}
              />
              <span class={styles.value}>{quality()}</span>
            </label>
          </Show>

          <Show when={format() === 'png8'}>
            <label class={styles.row}>
              <span class={styles.label}>颜色数</span>
              <input
                type="range"
                min="2"
                max="256"
                value={colors()}
                data-testid="export-colors"
                onInput={(event) => {
                  setColors(Number(event.currentTarget.value));
                }}
              />
              <span class={styles.value}>{colors()}</span>
            </label>
            <label class={styles.row}>
              <span class={styles.label}>仿色</span>
              <input
                type="checkbox"
                checked={dither()}
                data-testid="export-dither"
                onChange={(event) => {
                  setDither(event.currentTarget.checked);
                }}
              />
            </label>
          </Show>

          <Show when={format() !== 'psd'}>
            <div class={styles.row}>
              <span class={styles.label}>分辨率</span>
              <input
                type="number"
                min="1"
                class={styles.customScaleInput}
                value={width()}
                data-testid="export-width"
                onInput={(event) => {
                  setWidthPx(event.currentTarget.value);
                }}
              />
              <span class={styles.value}>×</span>
              <input
                type="number"
                min="1"
                class={styles.customScaleInput}
                value={height()}
                data-testid="export-height"
                onInput={(event) => {
                  setHeightPx(event.currentTarget.value);
                }}
              />
              <span class={styles.value}>px</span>
            </div>
            <div class={styles.row}>
              <span class={styles.label} />
              <label class={styles.scale}>
                <input
                  type="checkbox"
                  checked={lockAspect()}
                  disabled={props.documentSize === undefined}
                  data-testid="export-lock-aspect"
                  onChange={(event) => {
                    setLockAspect(event.currentTarget.checked);
                  }}
                />
                等比
              </label>
            </div>
            <div class={styles.row}>
              <span class={styles.label} />
              <For each={PRESET_SCALES}>
                {(scale) => (
                  <button
                    type="button"
                    class={styles.secondary}
                    disabled={props.documentSize === undefined}
                    data-testid={`export-preset-${scale}`}
                    onClick={() => {
                      applyPresetScale(scale);
                    }}
                  >
                    {scale}x
                  </button>
                )}
              </For>
            </div>
          </Show>

          <Show when={props.summary !== undefined}>
            <p class={styles.summary} data-testid="export-summary">
              {props.summary}
            </p>
          </Show>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="export-confirm"
              onClick={() => {
                const w = Number(width());
                const h = Number(height());
                const validSize = Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0;
                props.onExport({
                  format: format(),
                  quality: quality(),
                  scales: [],
                  customSizes: validSize ? [{ width: Math.round(w), height: Math.round(h) }] : [],
                  colors: colors(),
                  dither: dither(),
                });
              }}
            >
              导出
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
